import type { CRMUser, CommentMention } from '../types';
import { canAppearInMentionAutocomplete } from './mentionAffiliation';

/** Sentinel `mentions[].userId` for the @team channel broadcast. */
export const TEAM_MENTION_ID = '__team__';

export type MentionSuggestionItem =
  | { kind: 'team' }
  | { kind: 'user'; user: CRMUser };

export function isTeamMentionUserId(id: unknown): boolean {
  return id === TEAM_MENTION_ID;
}

export function commentHtmlHasTeamMention(html: string | null | undefined): boolean {
  if (!html) return false;
  return /data-user-id=["']__team__["']/.test(html) || /data-team=["']true["']/.test(html);
}

export function mentionsIncludeTeam(mentions: CommentMention[] | null | undefined): boolean {
  if (!Array.isArray(mentions)) return false;
  return mentions.some((m) => isTeamMentionUserId(m?.userId));
}

/** Show @team when the query is empty or a prefix of "team". */
export function teamMentionQueryMatches(query: string): boolean {
  const q = (query || '').toLowerCase();
  return !q || 'team'.startsWith(q);
}

export function mentionPartnerRank(
  user: Pick<CRMUser, 'uid' | 'role'>,
  assignedArchitectUids?: Iterable<string> | null,
): number {
  const assigned = new Set(assignedArchitectUids || []);
  if (assigned.has(user.uid)) return 0;
  const role = (user.role || '').toLowerCase();
  if (role === 'architect') return 1;
  if (role === 'designer') return 2;
  if (role === 'engineer') return 3;
  return 4;
}

export function buildMentionSuggestionItems(
  users: CRMUser[],
  query: string,
  opts?: {
    enableTeamMention?: boolean;
    viewerRole?: string | null;
    prioritizeDesignPartners?: boolean;
    assignedArchitectUids?: string[] | null;
  },
): MentionSuggestionItem[] {
  const q = (query || '').toLowerCase();
  const viewerRole = opts?.viewerRole;
  const matched = users
    .filter((u) => canAppearInMentionAutocomplete(u, viewerRole))
    .filter((u) =>
      (u.displayName || '').toLowerCase().includes(q)
      || (u.email || '').toLowerCase().includes(q),
    );

  const assigned = opts?.assignedArchitectUids || [];
  const sorted = opts?.prioritizeDesignPartners
    ? [...matched].sort((a, b) => {
      const d = mentionPartnerRank(a, assigned) - mentionPartnerRank(b, assigned);
      return d || (a.displayName || '').localeCompare(b.displayName || '');
    })
    : matched;

  const people = sorted
    .slice(0, opts?.prioritizeDesignPartners ? 16 : 8)
    .map((user): MentionSuggestionItem => ({ kind: 'user', user }));

  if (opts?.enableTeamMention && teamMentionQueryMatches(query)) {
    return [{ kind: 'team' }, ...people];
  }
  return people;
}

export function collectThreadParticipantIds(
  comments: Array<{
    authorId?: string | null;
    bodyHtml?: string | null;
    mentions?: Array<{ userId?: string | null }> | null;
  }> | null | undefined,
): string[] {
  const ids = new Set<string>();
  const htmlUserIdRe = /data-user-id=["']([^"']+)["']/g;
  for (const comment of comments || []) {
    const authorId = (comment.authorId || '').trim();
    if (authorId && !isTeamMentionUserId(authorId)) ids.add(authorId);
    if (Array.isArray(comment.mentions)) {
      for (const mention of comment.mentions) {
        const uid = (mention?.userId || '').trim();
        if (uid && !isTeamMentionUserId(uid)) ids.add(uid);
      }
    }
    const html = comment.bodyHtml || '';
    htmlUserIdRe.lastIndex = 0;
    let match: RegExpExecArray | null = htmlUserIdRe.exec(html);
    while (match) {
      const uid = (match[1] || '').trim();
      if (uid && !isTeamMentionUserId(uid)) ids.add(uid);
      match = htmlUserIdRe.exec(html);
    }
  }
  return Array.from(ids);
}

/** Assignees/AR plus people already in this thread (authors and @mentions). */
export function collectTeamChannelUserIds(args: {
  notifyUserIds?: string[] | null;
  comments?: Array<{
    authorId?: string | null;
    bodyHtml?: string | null;
    mentions?: Array<{ userId?: string | null }> | null;
  }> | null;
}): string[] {
  const ids = new Set<string>();
  for (const id of args.notifyUserIds || []) {
    const uid = (id || '').trim();
    if (uid && !isTeamMentionUserId(uid)) ids.add(uid);
  }
  for (const uid of collectThreadParticipantIds(args.comments)) ids.add(uid);
  return Array.from(ids);
}

export function teamChannelMemberNames(
  userIds: string[],
  crmUsers: Array<Pick<CRMUser, 'uid' | 'displayName'>>,
  authorUid?: string | null,
): string[] {
  const author = (authorUid || '').trim();
  const seen = new Set<string>();
  const names: string[] = [];
  for (const id of userIds) {
    if (!id || id === author || seen.has(id) || isTeamMentionUserId(id)) continue;
    seen.add(id);
    const user = crmUsers.find((u) => u.uid === id);
    const name = (user?.displayName || '').trim() || id.slice(0, 8);
    names.push(name);
  }
  return names.sort((a, b) => a.localeCompare(b));
}
